import { useEffect, useMemo, useRef, useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  useCaptureCards,
  useCaptureCardStats,
  useCaptureCardAccounts,
  useCreateCaptureCard,
  useUpdateCaptureCard,
  useDeleteCaptureCard,
  useToggleCaptureCard,
  useBulkUpdateUrl,
  useImportPresets,
  useBulkActivate,
  useBulkDeactivate,
  useBulkAssignAll,
  type CaptureCardButton,
  type CaptureCard,
  type CaptureCardPayload,
} from '@/api/hooks/useCaptureCards'
import { usePresets, type BroadcastPreset } from '@/api/hooks/usePresets'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ImageIcon,
  X,
  ExternalLink,
  Power,
  PowerOff,
  Search,
  Check,
  Sparkles,
  Shuffle,
  Facebook,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Link2,
  Download,
  Zap,
  CheckSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FolderPickerModal } from '@/components/FolderPickerModal'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const SAFE_PREVIEW_STORAGE_KEY = 'capture_cards_safe_preview'
const LIVE_CLICKS_REFRESH_ACTIVE_MS = 60 * 1000
const LIVE_CLICKS_REFRESH_IDLE_MS = 10 * 60 * 1000
const EMPTY_BUTTON: CaptureCardButton = { text: '', type: 'url', value: '' }

const createEmptyForm = (): CaptureCardPayload => ({
  name: '',
  title: '',
  subtitle: '',
  imageUrl: '',
  imageHasFilter: false,
  buttons: [{ ...EMPTY_BUTTON }],
  publicReplyText: '',
  isActive: true,
  accountIds: [],
})

export function CaptureCards() {
  const { t } = useLanguage()
  const [isPageVisible, setIsPageVisible] = useState(() => document.visibilityState === 'visible')
  const liveRefreshMs = isPageVisible ? LIVE_CLICKS_REFRESH_ACTIVE_MS : LIVE_CLICKS_REFRESH_IDLE_MS
  const { data: cards, isLoading } = useCaptureCards(liveRefreshMs)
  const { data: stats } = useCaptureCardStats(liveRefreshMs)
  const { data: accounts } = useCaptureCardAccounts()
  const create = useCreateCaptureCard()
  const update = useUpdateCaptureCard()
  const del = useDeleteCaptureCard()
  const toggle = useToggleCaptureCard()
  const bulkUrl = useBulkUpdateUrl()
  const importPresets = useImportPresets()
  const bulkActivate = useBulkActivate()
  const bulkDeactivate = useBulkDeactivate()
  const bulkAssignAll = useBulkAssignAll()
  const { data: presets } = usePresets()

  const [bulkUrlOpen, setBulkUrlOpen] = useState(false)
  const [bulkUrlValue, setBulkUrlValue] = useState('')
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [selectedPresetIds, setSelectedPresetIds] = useState<Set<string>>(new Set())

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CaptureCard | null>(null)
  const [form, setForm] = useState<CaptureCardPayload>(createEmptyForm)
  const [accountSearch, setAccountSearch] = useState('')
  const [uploading, setUploading] = useState(false)
  const [folderOpen, setFolderOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['player'])
  const toggleCardFilter = (f: string) => setSelectedFilters(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])
  const [safePreview, setSafePreview] = useState(true)
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const stored = window.localStorage.getItem(SAFE_PREVIEW_STORAGE_KEY)
    if (stored !== null) {
      setSafePreview(stored === 'true')
    }
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const toggleSafePreview = () => {
    setSafePreview(prev => {
      const next = !prev
      window.localStorage.setItem(SAFE_PREVIEW_STORAGE_KEY, String(next))
      return next
    })
  }

  const openCreate = () => {
    setEditing(null)
    setForm(createEmptyForm())
    setAccountSearch('')
    setModalOpen(true)
  }

  const openEdit = (card: CaptureCard) => {
    setEditing(card)
    setForm({
      name: card.name,
      title: card.title,
      subtitle: card.subtitle ?? '',
      imageUrl: card.imageUrl,
      imageHasFilter: card.imageHasFilter,
      buttons: card.buttons?.length ? card.buttons : [{
        text: card.buttonText,
        type: card.buttonType ?? 'url',
        value: card.buttonType === 'postback' ? (card.buttonPayload ?? '') : (card.buttonUrl ?? ''),
      }],
      publicReplyText: card.publicReplyText ?? '',
      isActive: card.isActive,
      accountIds: card.accountIds,
    })
    setAccountSearch('')
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error(t('Dê um nome ao card'))
    if (!form.title.trim()) return toast.error(t('Preencha o título'))
    if (!form.imageUrl.trim()) return toast.error(t('Envie uma imagem'))
    if (!form.accountIds || form.accountIds.length === 0) return toast.error(t('Selecione ao menos uma página'))

    const buttons = (form.buttons ?? [])
      .map(button => ({
        text: button.text.trim(),
        type: button.type,
        value: button.value.trim(),
      }))
      .filter(button => button.text && button.value)

    if (buttons.length === 0) {
      return toast.error(t('Configure ao menos um botão'))
    }

    const payload: CaptureCardPayload = {
      ...form,
      buttons,
      buttonText: buttons[0]?.text,
      buttonUrl: buttons[0]?.type === 'url' ? buttons[0]?.value : null,
      subtitle: form.subtitle?.trim() || null,
      publicReplyText: form.publicReplyText?.trim() || null,
    }

    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, payload })
        toast.success(t('Card atualizado'))
      } else {
        await create.mutateAsync(payload)
        toast.success(t('Card criado'))
      }
      setModalOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Erro ao salvar'))
    }
  }

  const handleDelete = async (card: CaptureCard) => {
    if (!confirm(t(`Remover "${card.name}"?`))) return
    try {
      await del.mutateAsync(card.id)
      toast.success(t('Removido'))
    } catch {
      toast.error(t('Erro ao remover'))
    }
  }

  const handleToggle = async (card: CaptureCard) => {
    try {
      await toggle.mutateAsync(card.id)
    } catch {
      toast.error(t('Erro ao alternar status'))
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const activeFilters = selectedFilters.length > 0 ? selectedFilters : ['none']
      const data = await api.upload(file, { filterType: activeFilters })
      const fullUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`
      setForm(f => ({ ...f, imageUrl: fullUrl, imageHasFilter: selectedFilters.length > 0 }))
      toast.success(t('Imagem enviada'))
    } catch {
      toast.error(t('Erro no upload'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const toggleAccount = (id: string) => {
    setForm(f => {
      const curr = f.accountIds ?? []
      return {
        ...f,
        accountIds: curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id],
      }
    })
  }

  const selectAll = () => {
    if (!accounts) return
    setForm(f => ({ ...f, accountIds: accounts.map(a => a.id) }))
  }
  const clearAll = () => setForm(f => ({ ...f, accountIds: [] }))

  const filteredAccounts = useMemo(() => {
    if (!accounts) return []
    const q = accountSearch.trim().toLowerCase()
    if (!q) return accounts
    return accounts.filter(a =>
      a.name.toLowerCase().includes(q) || a.pageId.includes(q),
    )
  }, [accounts, accountSearch])

  const accountNameMap = useMemo(() => {
    const map = new Map<string, string>()
    accounts?.forEach(a => map.set(a.id, a.name))
    return map
  }, [accounts])

  const toggleExpandedCard = (cardId: string) => {
    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }))
  }

  const updateButton = (index: number, patch: Partial<CaptureCardButton>) => {
    setForm(current => {
      const buttons = [...(current.buttons ?? [])]
      const base = buttons[index] ?? { ...EMPTY_BUTTON }
      const nextButton = { ...base, ...patch }
      if (patch.type === 'url' && !patch.value && base.type !== 'url') {
        nextButton.value = ''
      }
      buttons[index] = nextButton
      return { ...current, buttons }
    })
  }

  const addSecondaryButton = () => {
    setForm(current => {
      const buttons = [...(current.buttons ?? [])]
      if (buttons.length >= 2) return current
      buttons.push({ ...EMPTY_BUTTON, type: 'postback' })
      return { ...current, buttons }
    })
  }

  const removeSecondaryButton = () => {
    setForm(current => ({ ...current, buttons: (current.buttons ?? []).slice(0, 1) }))
  }

  const handleImportPresets = async () => {
    if (selectedPresetIds.size === 0) { toast.error('Selecione pelo menos 1 preset'); return }
    try {
      const res = await importPresets.mutateAsync([...selectedPresetIds])
      toast.success(`${res.created} card(s) importado(s)`)
      setImportModalOpen(false)
      setSelectedPresetIds(new Set())
    } catch { toast.error('Erro ao importar') }
  }

  const handleBulkActivate = async () => {
    try {
      const res = await bulkActivate.mutateAsync()
      toast.success(res.activated > 0 ? `${res.activated} card(s) ativado(s)` : 'Todos ja estao ativos')
    } catch { toast.error('Erro ao ativar') }
  }

  const handleBulkDeactivate = async () => {
    if (!confirm('Pausar TODOS os cards? O envio para de imediato. Reativa com "Ativar todos".')) return
    try {
      const res = await bulkDeactivate.mutateAsync()
      toast.success(res.deactivated > 0 ? `${res.deactivated} card(s) pausado(s)` : 'Nenhum card ativo')
    } catch { toast.error('Erro ao pausar') }
  }

  const handleBulkAssignAll = async () => {
    if (!confirm('Atribuir todos os cards a todas as paginas?')) return
    try {
      const res = await bulkAssignAll.mutateAsync()
      toast.success(`${res.assigned} atribuicao(es) criada(s) — ${res.cards} cards x ${res.accounts} paginas`)
    } catch { toast.error('Erro ao atribuir') }
  }

  const togglePresetId = (id: string) => {
    setSelectedPresetIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('Card de Captura')}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Button
            variant="outline" size="sm"
            onClick={() => { setImportModalOpen(true); setSelectedPresetIds(new Set()) }}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            {t('Importar do Broadcast')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleBulkActivate}
            disabled={bulkActivate.isPending}
            className="gap-1.5"
          >
            {bulkActivate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {t('Ativar todos')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleBulkDeactivate}
            disabled={bulkDeactivate.isPending}
            className="gap-1.5 text-amber-600 hover:text-amber-700"
          >
            {bulkDeactivate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
            {t('Pausar todos')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleBulkAssignAll}
            disabled={bulkAssignAll.isPending}
            className="gap-1.5"
          >
            {bulkAssignAll.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckSquare className="h-3.5 w-3.5" />}
            {t('Aplicar a todas paginas')}
          </Button>
          <div className="w-px h-6 bg-border" />
          <Button
            type="button"
            variant="outline" size="sm"
            onClick={toggleSafePreview}
            className="gap-1.5"
          >
            {safePreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {safePreview ? t('Safe ativo') : t('Safe desligado')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => { setBulkUrlOpen(true); setBulkUrlValue('') }}
            className="gap-1.5"
          >
            <Link2 className="h-3.5 w-3.5" />
            {t('Alterar link')}
          </Button>
          <Button onClick={openCreate} size="sm" className="gap-1.5 shrink-0">
            <Plus className="h-3.5 w-3.5" />
            {t('Novo card')}
          </Button>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-lg border border-border/40 bg-muted/10 p-4 text-xs text-muted-foreground flex gap-3">
        <Shuffle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <div>
          <strong className="text-foreground">{t('Como funciona:')}</strong>{' '}
          {t('quando alguém comentar em um post de uma página atribuída, o sistema sorteia aleatoriamente um dos cards ativos e envia no direct como um Generic Template (imagem, título, subtítulo e botão clicável). Cards com mais modelos distribuem melhor o risco.')}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)] gap-4">
          <div className="rounded-xl border border-border/40 bg-card p-4">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold text-foreground">{t('Cliques dos cards')}</h2>
                <p className="text-xs text-muted-foreground">
                  {t('O contador de hoje reinicia automaticamente a cada novo dia.')}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="outline" className="text-[10px]">
                  {stats.todayClicks} {t('hoje')}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {isPageVisible ? t('Atualiza a cada 1 min') : t('Atualiza a cada 10 min')}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: t('Cliques hoje'), value: stats.todayClicks },
                { label: t('Total de cliques'), value: stats.totalClicks },
                { label: t('Cards com clique hoje'), value: stats.cardsWithClicksToday },
              ].map(item => (
                <div key={item.label} className="rounded-lg border border-border/30 bg-muted/10 p-3">
                  <div className="text-lg font-semibold text-foreground">{item.value}</div>
                  <div className="text-[11px] text-muted-foreground">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chart}>
                  <defs>
                    <linearGradient id="captureClicksFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-chart-2)" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="var(--color-chart-2)" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={28} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                    formatter={(value) => [`${value ?? 0} ${t('cliques')}`, t('Cliques')]}
                    labelFormatter={(label) => `${t('Dia')} ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    stroke="var(--color-chart-2)"
                    strokeWidth={2}
                    fill="url(#captureClicksFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-border/40 bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground mb-1">{t('Mais clicados hoje')}</h2>
            <p className="text-xs text-muted-foreground mb-4">
              {t('Visão geral dos cards que mais puxaram clique no dia.')}
            </p>
            <div className="space-y-2">
              {stats.topCardsToday.length === 0 ? (
                <div className="rounded-lg border border-border/30 bg-muted/10 p-4 text-xs text-muted-foreground">
                  {t('Ainda sem cliques hoje')}
                </div>
              ) : stats.topCardsToday.map(card => (
                <div key={card.id} className="rounded-lg border border-border/30 bg-muted/10 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground truncate" title={card.name}>{card.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {card.totalClicks} {t('cliques totais')}
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {card.todayClicks} {t('hoje')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cards list */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : !cards || cards.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={t('Nenhum card criado')}
          description={t('Crie múltiplos modelos — o sistema rotaciona sozinho para cada novo comentário.')}
          action={{ label: t('Criar primeiro card'), onClick: openCreate }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map(card => (
            (() => {
              const assignedNames = card.accountIds
                .map(id => accountNameMap.get(id) ?? id)
                .filter(Boolean)
              const isExpanded = !!expandedCards[card.id]
              const visibleNames = isExpanded ? assignedNames : assignedNames.slice(0, 2)
              const hiddenCount = Math.max(0, assignedNames.length - visibleNames.length)

              return (
            <div
              key={card.id}
              className={cn(
                'group relative rounded-xl border border-border/40 bg-card overflow-hidden hover:border-primary/40 transition-all',
                !card.isActive && 'opacity-60',
              )}
            >
              {/* Preview image */}
              <div className="relative aspect-[1200/630] bg-muted overflow-hidden">
                {card.imageUrl ? (
                  <>
                    <img
                      src={card.imageUrl}
                      alt={card.name}
                      className={cn(
                        'h-full w-full object-cover transition-all duration-200',
                        safePreview && 'scale-[1.02] blur-xl brightness-75 saturate-75',
                      )}
                    />
                    {safePreview && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/10 backdrop-blur-[2px]">
                        <Badge variant="secondary" className="text-[10px]">
                          {t('Safe preview ativo')}
                        </Badge>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <ImageIcon className="h-8 w-8" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Badge variant={card.isActive ? 'default' : 'secondary'} className="text-[10px]">
                    {card.isActive ? t('Ativo') : t('Pausado')}
                  </Badge>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{card.name}</div>
                  <div className="font-semibold text-sm line-clamp-1">{card.title}</div>
                  {card.subtitle && (
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{card.subtitle}</div>
                  )}
                </div>

                {/* Button pills */}
                <div className="space-y-1.5">
                  {card.buttons.map((button, index) => (
                    <div key={`${card.id}-button-${index}`} className="flex items-center gap-1.5 text-xs rounded-md bg-muted/40 px-2.5 py-1.5 border border-border/30">
                      <ExternalLink className="h-3 w-3 text-primary shrink-0" />
                      <span className="truncate flex-1">{button.text}</span>
                      <Badge variant="outline" className="text-[9px] uppercase">
                        {button.type === 'postback' ? t('mensagem') : t('link')}
                      </Badge>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    <Facebook className="h-3 w-3 inline mr-1" />
                    {card.accountIds.length} {card.accountIds.length === 1 ? t('página') : t('páginas')}
                  </span>
                  <span>{card.sentCount} {t('envios')}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border border-border/30 bg-muted/10 px-2.5 py-2">
                    <div className="text-sm font-semibold text-foreground">{card.todayClicks}</div>
                    <div className="text-[10px] text-muted-foreground">{t('cliques hoje')}</div>
                  </div>
                  <div className="rounded-md border border-border/30 bg-muted/10 px-2.5 py-2">
                    <div className="text-sm font-semibold text-foreground">{card.clickCount}</div>
                    <div className="text-[10px] text-muted-foreground">{t('cliques totais')}</div>
                  </div>
                  <div className="rounded-md border border-border/30 bg-muted/10 px-2.5 py-2">
                    <div className="text-sm font-semibold text-foreground">
                      {card.sentCount > 0 ? `${((card.clickCount / card.sentCount) * 100).toFixed(1)}%` : '-'}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{t('CTR')}</div>
                  </div>
                </div>

                {assignedNames.length > 0 && (
                  <div className="rounded-lg border border-border/30 bg-muted/20 p-2.5 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        {t('Páginas configuradas')}
                      </div>
                      {assignedNames.length > 2 && (
                        <button
                          type="button"
                          onClick={() => toggleExpandedCard(card.id)}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
                        >
                          {isExpanded ? t('Recolher') : t('Expandir')}
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleNames.map(name => (
                        <Badge
                          key={`${card.id}-${name}`}
                          variant="secondary"
                          className="max-w-full px-2 py-0.5 text-[10px] font-normal"
                          title={name}
                        >
                          <span className="max-w-[220px] truncate">{name}</span>
                        </Badge>
                      ))}
                      {!isExpanded && hiddenCount > 0 && (
                        <Badge variant="outline" className="px-2 py-0.5 text-[10px]">
                          +{hiddenCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-1.5 pt-2 border-t border-border/30">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggle(card)}
                    className="flex-1 h-8 text-xs"
                  >
                    <Power className="h-3 w-3 mr-1" />
                    {card.isActive ? t('Pausar') : t('Ativar')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(card)} className="h-8 w-8 p-0">
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(card)} className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
              )
            })()
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('Editar card de captura') : t('Novo card de captura')}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
            {/* Form column */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs">{t('Nome interno')}</Label>
                <Input
                  placeholder={t('Ex: Finanças 1')}
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  maxLength={120}
                />
                <p className="text-[10px] text-muted-foreground mt-1">{t('Só você vê esse nome.')}</p>
              </div>

              <div>
                <Label className="text-xs">{t('Imagem')}</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                />
                {form.imageUrl.startsWith('__random__') ? (
                  <div className="mt-1 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5">
                    <Shuffle className="h-4 w-4 shrink-0 text-primary" />
                    <span className="flex-1 text-xs font-medium text-primary">{t('Foto aleatória da pasta')}</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, imageUrl: '' }))} className="text-destructive/70 hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : form.imageUrl ? (
                  <div className="relative mt-1 rounded-lg overflow-hidden border border-border/40">
                    <img
                      src={form.imageUrl}
                      alt="preview"
                      className={cn(
                        'w-full aspect-[1200/630] object-cover transition-all duration-200',
                        safePreview && 'scale-[1.02] blur-xl brightness-75 saturate-75',
                      )}
                    />
                    {safePreview && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/10 backdrop-blur-[2px]">
                        <Badge variant="secondary" className="text-[10px]">
                          {t('Safe preview ativo')}
                        </Badge>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, imageUrl: '' }))}
                      className="absolute top-2 right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="mt-1 w-full aspect-[1200/630] rounded-lg border-2 border-dashed border-border/50 bg-muted/20 hover:bg-muted/40 transition-all flex flex-col items-center justify-center gap-2 text-muted-foreground"
                  >
                    {uploading ? (
                      <><Loader2 className="h-6 w-6 animate-spin" /><span className="text-xs">{t('Enviando...')}</span></>
                    ) : (
                      <><ImageIcon className="h-8 w-8" /><span className="text-xs">{t('Clique para enviar')}</span></>
                    )}
                  </button>
                )}
                {!form.imageUrl.startsWith('__random__') && (
                  <button type="button" onClick={() => setFolderOpen(true)} className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/30 py-2 text-[11px] text-primary/80 hover:bg-primary/5 transition-all">
                    <Shuffle className="h-3.5 w-3.5" /> {t('Atribuir pasta de mídias')}
                  </button>
                )}
                <FolderPickerModal open={folderOpen} onOpenChange={setFolderOpen} onSelect={(id) => setForm(f => ({ ...f, imageUrl: '__random__:' + id }))} />

                <div className="flex items-center gap-2.5 flex-wrap mt-2">
                  <span className="text-xs text-muted-foreground">{t('Filtros:')}</span>
                  {[
                    { key: 'player', label: t('Player') },
                    { key: 'blur', label: t('Blur') },
                    { key: 'click', label: t('Clique') },
                    { key: 'sensitive', label: t('Sensivel') },
                  ].map(f => (
                    <label key={f.key} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={selectedFilters.includes(f.key)} onChange={() => toggleCardFilter(f.key)} className="h-3.5 w-3.5 rounded border-border" />
                      <span className="text-[11px] text-muted-foreground">{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">{t('Título (máx. 80)')}</Label>
                <Input
                  placeholder={t('Ex: Quer sair do vermelho?')}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value.slice(0, 80) }))}
                  maxLength={80}
                />
                <p className="text-[10px] text-muted-foreground mt-1 text-right">{form.title.length}/80</p>
              </div>

              <div>
                <Label className="text-xs">{t('Subtítulo (opcional, máx. 80)')}</Label>
                <Input
                  placeholder={t('Ex: Receba dicas práticas no direct')}
                  value={form.subtitle ?? ''}
                  onChange={e => setForm(f => ({ ...f, subtitle: e.target.value.slice(0, 80) }))}
                  maxLength={80}
                />
                <p className="text-[10px] text-muted-foreground mt-1 text-right">{(form.subtitle ?? '').length}/80</p>
              </div>

              <div className="space-y-3">
                {(form.buttons ?? []).map((button, index) => {
                  const isSecondary = index === 1
                  return (
                    <div key={`button-editor-${index}`} className="rounded-xl border border-border/30 bg-muted/10 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">{isSecondary ? t('Botão 2 (opcional)') : t('Botão 1')}</Label>
                        {isSecondary ? (
                          <button type="button" onClick={removeSecondaryButton} className="text-[10px] text-muted-foreground hover:underline">
                            {t('Remover')}
                          </button>
                        ) : (
                          <Badge variant="secondary" className="text-[9px]">{t('Obrigatório')}</Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">{t('Texto do botão (máx. 20)')}</Label>
                          <Input
                            placeholder={t('Ex: Quero saber')}
                            value={button.text}
                            onChange={e => updateButton(index, { text: e.target.value.slice(0, 20) })}
                            maxLength={20}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('Ação')}</Label>
                          <div className="mt-1 grid grid-cols-2 gap-2">
                            {([
                              { value: 'url', label: t('Link') },
                              { value: 'postback', label: t('Disparar mensagem') },
                            ] as const).map(option => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => updateButton(index, { type: option.value })}
                                className={cn(
                                  'h-9 rounded-lg border text-xs transition-colors',
                                  button.type === option.value
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border/40 bg-background hover:bg-muted/30',
                                )}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">
                          {button.type === 'postback' ? t('Payload / texto interno') : t('Link do botão')}
                        </Label>
                        <Input
                          placeholder={button.type === 'postback' ? t('Ex: CAPTURA_QUERO_RECEBER') : 'https://...'}
                          value={button.value}
                          onChange={e => updateButton(index, { value: e.target.value })}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {button.type === 'postback'
                            ? t('Esse valor chega no webhook e pode ser tratado como intenção do lead.')
                            : t('Ao clicar, o lead abre o destino configurado e o clique entra nas estatísticas.')}
                        </p>
                      </div>
                    </div>
                  )
                })}

                {(form.buttons?.length ?? 0) < 2 && (
                  <Button type="button" variant="outline" size="sm" onClick={addSecondaryButton} className="w-full">
                    {t('Adicionar segundo botão')}
                  </Button>
                )}
              </div>

              <div>
                <Label className="text-xs">{t('Resposta pública (opcional)')}</Label>
                <Textarea
                  placeholder={t('Ex: Acabei de te mandar lá no direct 💌')}
                  value={form.publicReplyText ?? ''}
                  onChange={e => setForm(f => ({ ...f, publicReplyText: e.target.value.slice(0, 255) }))}
                  maxLength={255}
                  rows={2}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t('Texto que aparece como resposta pública embaixo do comentário. Deixe vazio para não postar nada público.')}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={!!form.isActive}
                  onCheckedChange={v => setForm(f => ({ ...f, isActive: v }))}
                />
                <Label className="text-xs">{t('Card ativo (entra na rotação)')}</Label>
              </div>
            </div>

            {/* Preview + accounts column */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs mb-2 block">{t('Pré-visualização (estilo Messenger)')}</Label>
                <MessengerPreview
                  title={form.title || t('Título aparece aqui')}
                  subtitle={form.subtitle ?? ''}
                  imageUrl={form.imageUrl}
                  buttons={(form.buttons ?? []).filter(button => button.text.trim()).map(button => ({
                    ...button,
                    text: button.text || t('Botão'),
                  }))}
                  safePreview={safePreview}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">
                    {t('Páginas do Facebook')}{' '}
                    <span className="text-muted-foreground">
                      ({(form.accountIds ?? []).length} {t('selecionadas')})
                    </span>
                  </Label>
                  <div className="flex gap-1">
                    <button type="button" onClick={selectAll} className="text-[10px] text-primary hover:underline">{t('Todas')}</button>
                    <span className="text-[10px] text-muted-foreground">·</span>
                    <button type="button" onClick={clearAll} className="text-[10px] text-muted-foreground hover:underline">{t('Nenhuma')}</button>
                  </div>
                </div>

                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder={t('Buscar página...')}
                    value={accountSearch}
                    onChange={e => setAccountSearch(e.target.value)}
                    className="pl-8 h-9 text-xs"
                  />
                </div>

                <div className="max-h-60 overflow-y-auto rounded-lg border border-border/40 bg-muted/5">
                  {filteredAccounts.length === 0 ? (
                    <div className="p-4 text-center text-xs text-muted-foreground">
                      {t('Nenhuma página encontrada')}
                    </div>
                  ) : (
                    filteredAccounts.map(acc => {
                      const selected = form.accountIds?.includes(acc.id)
                      const paused = !acc.isActive || !acc.fbActive
                      return (
                        <button
                          key={acc.id}
                          type="button"
                          onClick={() => toggleAccount(acc.id)}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted/30 transition-colors border-b border-border/20 last:border-0',
                            selected && 'bg-primary/10 hover:bg-primary/15',
                          )}
                        >
                          <div className={cn(
                            'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                            selected ? 'bg-primary border-primary' : 'border-border/60',
                          )}>
                            {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <Facebook className="h-3 w-3 text-[#1877f2] shrink-0" />
                          <span className={cn('flex-1 truncate', paused && 'text-muted-foreground')}>{acc.name}</span>
                          {paused && (
                            <Badge variant="secondary" className="text-[9px] h-4 px-1.5">{t('pausada')}</Badge>
                          )}
                        </button>
                      )
                    })
                  )}
                </div>

                {accountNameMap.size > 0 && (form.accountIds?.length ?? 0) > 0 && (
                  <div className="mt-2 rounded-lg border border-border/30 bg-muted/10 p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                      {t('Selecionadas:')}
                    </div>
                    <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto pr-1">
                      {(form.accountIds ?? []).map(id => accountNameMap.get(id) ?? id).filter(Boolean).map(name => (
                        <Badge
                          key={`selected-${name}`}
                          variant="secondary"
                          className="max-w-full px-2 py-0.5 text-[10px] font-normal"
                          title={name}
                        >
                          <span className="max-w-[220px] truncate">{name}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border/30">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {(create.isPending || update.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? t('Salvar alterações') : t('Criar card')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk update URL dialog */}
      <Dialog open={bulkUrlOpen} onOpenChange={setBulkUrlOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Alterar link de todos os cards')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              {t('O novo link será aplicado ao botão principal de todos os cards que usam link (tipo URL).')}
            </p>
            <div>
              <Label className="text-xs">{t('Novo link')}</Label>
              <Input
                placeholder="https://exemplo.com/novo-link"
                value={bulkUrlValue}
                onChange={e => setBulkUrlValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && bulkUrlValue.trim()) {
                    bulkUrl.mutate({ newUrl: bulkUrlValue.trim() }, {
                      onSuccess: (data) => {
                        toast.success(`${data.updated} card(s) atualizado(s)`)
                        setBulkUrlOpen(false)
                      },
                      onError: (err) => toast.error(err.message),
                    })
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setBulkUrlOpen(false)}>
                {t('Cancelar')}
              </Button>
              <Button
                disabled={!bulkUrlValue.trim() || bulkUrl.isPending}
                onClick={() => {
                  bulkUrl.mutate({ newUrl: bulkUrlValue.trim() }, {
                    onSuccess: (data) => {
                      toast.success(`${data.updated} card(s) atualizado(s)`)
                      setBulkUrlOpen(false)
                    },
                    onError: (err) => toast.error(err.message),
                  })
                }}
              >
                {bulkUrl.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('Aplicar em todos')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Presets Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('Importar Templates do Broadcast')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('Selecione os presets para criar cards de captura independentes.')}
            </p>
            {presets && presets.length > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={() => {
                  if (selectedPresetIds.size === presets.length) {
                    setSelectedPresetIds(new Set())
                  } else {
                    setSelectedPresetIds(new Set(presets.map(p => p.id)))
                  }
                }}
                className="text-xs shrink-0"
              >
                {selectedPresetIds.size === presets?.length ? t('Desmarcar todos') : t('Selecionar todos')}
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 mt-2 min-h-0">
            {presets && presets.length > 0 ? presets.map(preset => {
              const selected = selectedPresetIds.has(preset.id)
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => togglePresetId(preset.id)}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-all',
                    selected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-primary/30 hover:bg-muted/30'
                  )}
                >
                  <div className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all',
                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30'
                  )}>
                    {selected && <Check className="h-3 w-3" />}
                  </div>
                  {preset.imageUrl && (
                    <img src={preset.imageUrl} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">{preset.emoji}</span>
                      <span className="text-sm font-medium truncate">{preset.name}</span>
                      <Badge variant="outline" className="text-[9px] shrink-0">
                        {preset.mode === 'card' ? 'Card' : 'Texto'}
                      </Badge>
                      {preset.isGlobal && <Badge variant="secondary" className="text-[9px] shrink-0">Global</Badge>}
                    </div>
                    {preset.cardTitle && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{preset.cardTitle}</p>
                    )}
                  </div>
                </button>
              )
            }) : (
              <p className="text-sm text-muted-foreground text-center py-8">Nenhum preset encontrado</p>
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t">
            <span className="text-xs text-muted-foreground">
              {selectedPresetIds.size} selecionado(s)
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setImportModalOpen(false)}>
                {t('Cancelar')}
              </Button>
              <Button
                size="sm"
                disabled={selectedPresetIds.size === 0 || importPresets.isPending}
                onClick={handleImportPresets}
                className="gap-1.5"
              >
                {importPresets.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {t('Importar')} {selectedPresetIds.size > 0 && `(${selectedPresetIds.size})`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* Messenger-style generic template preview */
function MessengerPreview({
  title,
  subtitle,
  imageUrl,
  buttons,
  safePreview,
}: {
  title: string
  subtitle: string
  imageUrl: string
  buttons: CaptureCardButton[]
  safePreview: boolean
}) {
  return (
    <div className="rounded-xl bg-[#0b1424] border border-border/40 p-4">
      <div className="flex items-start gap-2 mb-3">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#1877f2] to-[#0c63d4] flex items-center justify-center text-white text-[10px] font-semibold">FB</div>
        <div className="flex-1">
          <div className="text-[10px] text-white/50">{'Messenger · agora'}</div>
          <div className="mt-2 rounded-2xl overflow-hidden bg-white max-w-[280px]">
            {imageUrl ? (
              <div className="aspect-[1200/630] bg-muted overflow-hidden">
                <div className="relative h-full w-full">
                  <img
                    src={imageUrl}
                    alt=""
                    className={cn(
                      'h-full w-full object-cover transition-all duration-200',
                      safePreview && 'scale-[1.02] blur-xl brightness-75 saturate-75',
                    )}
                  />
                  {safePreview && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/10 backdrop-blur-[2px]">
                      <Badge variant="secondary" className="text-[10px]">
                        Safe preview ativo
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="aspect-[1200/630] bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-400">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className="p-3 border-t border-slate-100">
              <div className="text-[13px] font-semibold text-slate-900 line-clamp-2 leading-tight">{title}</div>
              {subtitle && (
                <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{subtitle}</div>
              )}
            </div>
            {(buttons.length > 0 ? buttons : [{ text: 'Botão', type: 'url', value: '' }]).map((button, index) => (
              <button
                key={`preview-button-${index}`}
                type="button"
                className="w-full text-center text-[13px] font-medium text-[#1877f2] py-2.5 border-t border-slate-100 hover:bg-slate-50 transition-colors"
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
